// Where a pasted or duplicated clip can legally go.
//
// Overlap is the one rejection this client can predict perfectly: it depends
// only on state we already hold. So rather than propose a colliding clip and
// eat a rejection, we resolve it here, against the room's OWN rule — the
// half-open, ASSERT_EPS-tolerant test in shared/sync/merge-engine.ts.
//
// The merge engine's comment that an overlap "lands on the following cycle"
// assumes a proposer watching Live truncate the clip and re-proposing what it
// sees. A browser has no Live to watch, so for us an overlap is simply a no.

import { ASSERT_EPS } from "@shared/sync/canonical.js";
import { arrangementClipsOfTrack, type CanonicalRoom, type CanonicalTrackState } from "@shared/sync/state.js";
import { normalizeClipName } from "@shared/library.js";
import { parseTrackName } from "@shared/sync/track-id.js";

/** How far forward we'll hunt for a gap before giving up, in beats. */
const SEARCH_HORIZON = 1024;

const NOTHING_IGNORED: ReadonlySet<string> = new Set<string>();

/**
 * The room's rule, restated, against everything on the lane EXCEPT `ignore`.
 *
 * A whole set of exemptions rather than one, because a selection moves
 * rigidly: a run of clips nudged one beat along is forever running into its
 * own next member, and testing them one at a time would call every such move
 * illegal.
 */
export function collidesExcept(
  room: CanonicalRoom,
  trackId: string,
  startTime: number,
  duration: number,
  ignore: ReadonlySet<string>,
): boolean {
  const end = startTime + duration;
  // Session View clips sit at startTime 0 by convention and occupy no
  // timeline extent — the room's own rule (merge-engine `overlapping`).
  return arrangementClipsOfTrack(room, trackId).some((other) => {
    if (ignore.has(other.id)) return false;
    const otherEnd = other.startTime + other.duration;
    return startTime < otherEnd - ASSERT_EPS && other.startTime < end - ASSERT_EPS;
  });
}

/** The room's rule, restated. Touching end-to-start is legal; intersecting isn't. */
export function collides(
  room: CanonicalRoom,
  trackId: string,
  startTime: number,
  duration: number,
  ignoreClipId: string | null = null,
): boolean {
  return collidesExcept(
    room,
    trackId,
    startTime,
    duration,
    ignoreClipId === null ? NOTHING_IGNORED : new Set([ignoreClipId]),
  );
}

/**
 * The first position at or after `from` where a clip of `duration` fits on this
 * lane. Walks the lane's occupied spans in order rather than probing blindly, so
 * a dense lane costs one pass. Returns null when nothing fits inside the horizon
 * — the caller should tell the user rather than propose something doomed.
 */
export function firstFreeSlot(
  room: CanonicalRoom,
  trackId: string,
  from: number,
  duration: number,
  ignoreClipId: string | null = null,
): number | null {
  let at = Math.max(0, from);
  const limit = at + SEARCH_HORIZON;
  const occupied = arrangementClipsOfTrack(room, trackId).filter((c) => c.id !== ignoreClipId);

  for (const other of occupied) {
    if (at > limit) return null;
    const otherEnd = other.startTime + other.duration;
    if (otherEnd <= at + ASSERT_EPS) continue; // entirely behind us
    // A gap that reaches this clip's start is only usable if the whole clip fits.
    if (other.startTime >= at + duration - ASSERT_EPS) return at;
    at = otherEnd;
  }
  return at <= limit ? at : null;
}

/**
 * Which lane a library clip should land on. Deterministic, in this order:
 * the selected clip's lane (the producer pointed at it), else the audio lane
 * whose display name matches the entry's `trackName` (library names are
 * token-stripped + bounce-normalized — shared/library.ts buildLibrary — so
 * room names get the same treatment before comparing), else null: the caller
 * creates a lane named after the entry. MIDI lanes never qualify — an audio
 * create on one is a kind mismatch the room would refuse.
 */
export function placementLane(
  room: CanonicalRoom,
  entryTrackName: string,
  selectedClipId: string | null,
): CanonicalTrackState | null {
  const audioLane = (t: CanonicalTrackState): boolean => (t.trackKind ?? "audio") === "audio";

  if (selectedClipId !== null) {
    const clip = room.clips.find((c) => c.id === selectedClipId);
    const lane = clip ? room.tracks.find((t) => t.id === clip.trackId) : undefined;
    if (lane && audioLane(lane)) return lane;
  }

  const wanted = entryTrackName.trim().toLowerCase();
  return (
    room.tracks.find(
      (t) =>
        audioLane(t) &&
        normalizeClipName(parseTrackName(t.name).display).trim().toLowerCase() === wanted,
    ) ?? null
  );
}

/** Snap to the editing grid. Sub-tick jitter would cost a rebuild in Live. */
export function snap(beats: number, grid: number): number {
  if (grid <= 0) return beats;
  return Math.round(beats / grid) * grid;
}

/**
 * Snap a position to the nearest of `edges` within `threshold` beats, else
 * leave it. What a drag does with the grid off: clip starts and ends are
 * the edges worth landing on, and a near miss is a gap nobody wanted.
 */
export function snapToEdges(value: number, edges: readonly number[], threshold: number): number {
  let best = value;
  let bestDist = threshold;
  for (const e of edges) {
    const d = Math.abs(e - value);
    if (d < bestDist) {
      bestDist = d;
      best = e;
    }
  }
  return best;
}
